/**
 * AdminSiteSettings — CMS dynamique de la page d'accueil (V2.5)
 * Utilise : /api/v1/admin/site-settings (GET, PATCH)
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Eye, EyeOff, Image, FileText, LayoutList, RefreshCw } from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { api, formatApiError } from "../services/api";
import { invalidateSiteSettingsCache } from "../hooks/useSiteSettings";

const SECTION_LABELS = {
  mission:     "Notre Mission",
  comment:     "Comment ça marche",
  coach_ia:    "Coach IA",
  passeport:   "Passeport AFRICTAL",
  partenaires: "Partenaires",
  temoignages: "Témoignages",
  faq:         "FAQ",
  cta:         "Bannière CTA finale",
};

export default function AdminSiteSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v1/admin/site-settings");
      setSettings(data.settings);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = (section, field, value) => {
    setSettings((s) => ({
      ...s,
      [section]: { ...(s?.[section] || {}), [field]: value },
    }));
  };

  const toggleSection = (key) => {
    setSettings((s) => ({
      ...s,
      sections: {
        ...(s?.sections || {}),
        [key]: { ...(s?.sections?.[key] || {}), visible: !(s?.sections?.[key]?.visible !== false) },
      },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/v1/admin/site-settings", {
        hero: settings.hero,
        stats: settings.stats,
        mission: settings.mission,
        sections: settings.sections,
      });
      setSettings(data.settings);
      invalidateSiteSettingsCache();  // force re-fetch côté public
      toast.success("Page d'accueil mise à jour");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PrivateLayout title="Contenu Page d'Accueil">
        <div className="afr-card" style={{ padding: "3rem", display: "grid", placeItems: "center" }}>
          <Loader2 size={22} className="afr-spin" style={{ color: "var(--afr-primary)" }} />
        </div>
      </PrivateLayout>
    );
  }

  const s = settings || {};
  const h = s.hero || {};
  const st = s.stats || {};
  const m = s.mission || {};
  const sections = s.sections || {};

  return (
    <PrivateLayout
      title="Contenu Page d'Accueil"
      breadcrumbs={[{ label: "Administration" }, { label: "CMS · Accueil" }]}
      actions={
        <button type="button" onClick={save} className="afr-btn afr-btn--primary afr-btn--sm" disabled={saving} data-testid="cms-save">
          {saving ? <Loader2 size={14} className="afr-spin" /> : <Save size={14} />}
          Sauvegarder
        </button>
      }
    >
      <p className="afr-muted" style={{ marginBottom: "1.4rem", fontSize: "0.9rem" }}>
        Modifiez les textes, l'image de la bannière et la visibilité des sections de la page d'accueil publique — sans redéploiement.
        Les valeurs vides utilisent les textes par défaut (i18n).
      </p>

      {/* HERO */}
      <div className="afr-card" data-testid="cms-hero" style={{ padding: "1.2rem 1.4rem", marginBottom: "1rem" }}>
        <h3 style={{ margin: "0 0 0.9rem", fontFamily: "'Outfit',sans-serif", color: "var(--afr-secondary)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <FileText size={18} style={{ color: "var(--afr-primary)" }} /> Bannière Hero
        </h3>
        <div className="afr-grid" style={{ gridTemplateColumns: "1fr 1fr", gap: "0.8rem" }}>
          <Field label="Eyebrow" value={h.eyebrow} onChange={(v) => patch("hero", "eyebrow", v)} testId="cms-hero-eyebrow" placeholder="Laisser vide pour utiliser le texte par défaut" />
          <Field label="Titre — partie 1 (italique)" value={h.title_1} onChange={(v) => patch("hero", "title_1", v)} testId="cms-hero-title-1" />
          <Field label="Titre — partie 2" value={h.title_2} onChange={(v) => patch("hero", "title_2", v)} testId="cms-hero-title-2" />
          <Field label="Titre — partie 3 (italique)" value={h.title_3} onChange={(v) => patch("hero", "title_3", v)} testId="cms-hero-title-3" />
          <div style={{ gridColumn: "1 / -1" }}>
            <Field label="Sous-titre / Lead" value={h.lead} onChange={(v) => patch("hero", "lead", v)} testId="cms-hero-lead" multiline />
          </div>
          <Field label="Libellé CTA inscription" value={h.cta_signup_label} onChange={(v) => patch("hero", "cta_signup_label", v)} testId="cms-hero-cta-signup" />
          <Field label="Libellé CTA connexion" value={h.cta_login_label} onChange={(v) => patch("hero", "cta_login_label", v)} testId="cms-hero-cta-login" />
          <div style={{ gridColumn: "1 / -1" }}>
            <label className="afr-muted" style={{ fontSize: "0.78rem", display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.3rem" }}>
              <Image size={13} /> Image bannière (URL) — si vide, l'illustration SVG par défaut reste affichée
            </label>
            <input
              type="url"
              value={h.banner_image_url || ""}
              onChange={(e) => patch("hero", "banner_image_url", e.target.value)}
              placeholder="https://.../hero-banner.jpg"
              data-testid="cms-hero-banner"
              style={inputStyle}
            />
            {h.banner_image_url && (
              <div style={{ marginTop: "0.6rem", borderRadius: 10, overflow: "hidden", border: "1px solid var(--afr-border)", maxHeight: 220 }}>
                <img src={h.banner_image_url} alt="Aperçu bannière" style={{ width: "100%", display: "block", objectFit: "cover" }} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="afr-card" data-testid="cms-stats" style={{ padding: "1.2rem 1.4rem", marginBottom: "1rem" }}>
        <h3 style={{ margin: "0 0 0.9rem", fontFamily: "'Outfit',sans-serif", color: "var(--afr-secondary)" }}>Statistiques Hero</h3>
        <div className="afr-grid" style={{ gridTemplateColumns: "1fr 1fr 1fr", gap: "0.8rem" }}>
          <Field label="Talents" value={st.talents} onChange={(v) => patch("stats", "talents", v)} testId="cms-stat-talents" />
          <Field label="Offres" value={st.offers} onChange={(v) => patch("stats", "offers", v)} testId="cms-stat-offers" />
          <Field label="Pays" value={st.countries} onChange={(v) => patch("stats", "countries", v)} testId="cms-stat-countries" />
        </div>
      </div>

      {/* MISSION */}
      <div className="afr-card" data-testid="cms-mission" style={{ padding: "1.2rem 1.4rem", marginBottom: "1rem" }}>
        <h3 style={{ margin: "0 0 0.9rem", fontFamily: "'Outfit',sans-serif", color: "var(--afr-secondary)" }}>Section Mission</h3>
        <div className="afr-grid" style={{ gridTemplateColumns: "1fr", gap: "0.8rem" }}>
          <Field label="Titre" value={m.title} onChange={(v) => patch("mission", "title", v)} testId="cms-mission-title" />
          <Field label="Texte de présentation" value={m.lead} onChange={(v) => patch("mission", "lead", v)} testId="cms-mission-lead" multiline />
        </div>
      </div>

      {/* VISIBILITY */}
      <div className="afr-card" data-testid="cms-sections" style={{ padding: "1.2rem 1.4rem", marginBottom: "1rem" }}>
        <h3 style={{ margin: "0 0 0.9rem", fontFamily: "'Outfit',sans-serif", color: "var(--afr-secondary)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <LayoutList size={18} style={{ color: "var(--afr-primary)" }} /> Sections publiques (afficher / masquer)
        </h3>
        <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "0.6rem" }}>
          {Object.entries(SECTION_LABELS).map(([key, label]) => {
            const visible = sections[key]?.visible !== false;
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggleSection(key)}
                data-testid={`cms-section-${key}`}
                className="afr-card"
                style={{
                  padding: "0.7rem 0.9rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "0.5rem",
                  cursor: "pointer",
                  background: visible ? "color-mix(in oklab, var(--afr-primary) 8%, var(--afr-surface))" : "var(--afr-surface)",
                  borderColor: visible ? "color-mix(in oklab, var(--afr-primary) 30%, var(--afr-border))" : "var(--afr-border)",
                }}
              >
                <span style={{ fontFamily: "'Outfit',sans-serif", color: "var(--afr-secondary)", fontWeight: 600, fontSize: "0.9rem" }}>{label}</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", color: visible ? "var(--afr-primary)" : "var(--afr-muted)", fontSize: "0.78rem", fontWeight: 700 }}>
                  {visible ? <><Eye size={14} /> Visible</> : <><EyeOff size={14} /> Masquée</>}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
        <button type="button" onClick={load} className="afr-btn afr-btn--outline afr-btn--sm" data-testid="cms-reload">
          <RefreshCw size={14} /> Recharger
        </button>
        <button type="button" onClick={save} className="afr-btn afr-btn--primary afr-btn--sm" disabled={saving} data-testid="cms-save-bottom">
          {saving ? <Loader2 size={14} className="afr-spin" /> : <Save size={14} />}
          Sauvegarder les modifications
        </button>
      </div>
    </PrivateLayout>
  );
}


const inputStyle = {
  width: "100%",
  padding: "0.55rem 0.75rem",
  borderRadius: 8,
  border: "1px solid var(--afr-border)",
  background: "var(--afr-surface)",
  color: "var(--afr-secondary)",
  fontFamily: "'Outfit',sans-serif",
  fontSize: "0.9rem",
};

function Field({ label, value, onChange, placeholder, testId, multiline }) {
  return (
    <div>
      <label className="afr-muted" style={{ fontSize: "0.78rem", display: "block", marginBottom: "0.3rem" }}>{label}</label>
      {multiline ? (
        <textarea
          rows={3}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || "Laisser vide pour utiliser le texte par défaut"}
          data-testid={testId}
          style={{ ...inputStyle, resize: "vertical" }}
        />
      ) : (
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || "Laisser vide pour utiliser le texte par défaut"}
          data-testid={testId}
          style={inputStyle}
        />
      )}
    </div>
  );
}
